<script setup lang="ts">
    import { Company } from '@/types';
    import { useForm } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { useI18n } from 'vue-i18n';
    import {
        AlertDialog,
        AlertDialogTrigger,
        AlertDialogContent,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogCancel,
        AlertDialogAction,
    } from '@/components/ui/alert-dialog';
    import ActivateButton from '@/components/ActivateButton.vue';
    import DeactivateButton from '@/components/DeactivateButton.vue';
    import { computed } from 'vue';
    import { toast } from 'vue-sonner';
    import QueryKey from '@/enums/QueryKey';

    const { t } = useI18n();

    const queryClient = useQueryClient();

    const props = defineProps<{
        company: Company;
    }>();

    const form = useForm({
        id: props.company.id,
    });

    const isDeactivated = computed(() => !!props.company.deactivated_at);

    const type = computed(() => {
        return isDeactivated.value ? 'activate' : 'deactivate';
    });

    const path = computed(() => {
        return isDeactivated.value ? 'v2.companies.activate' : 'v2.companies.deactivate';
    });

    const action = () => {
        form.post(route(path.value), {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [QueryKey.COMPANIES] });
                toast.success(t(`companies.js.${type.value}.success`));
            },
            onError: () => {
                toast.error(t('forms.general.error'));
            },
        });
    };
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <ActivateButton
                v-if="isDeactivated"
                class="mr-auto"
            />
            <DeactivateButton
                v-else
                class="mr-auto"
            />
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t(`companies.js.${type}.title`) }}</AlertDialogTitle>
                <AlertDialogDescription>{{
                    t(`companies.js.${type}.content`)
                }}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t(`companies.js.${type}.no`) }}</AlertDialogCancel>
                <AlertDialogAction
                    @click="action"
                    :variant="isDeactivated ? 'default' : 'destructive'"
                >
                    {{ t(`companies.js.${type}.yes`) }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
